/**
 * Eircode ADVISORY hint (T400) — one shared lenient pattern for the kiosk.
 *
 * Mirrors the site form's inline EIRCODE_RE (InquiryForm.astro — the site copy
 * stays inline because the Astro island cannot import from this package; noted
 * as a known cross-package duplicate for the Autoaddress task to unify).
 *
 * ADVISORY ONLY **IN THIS PACKAGE**, by hard requirement: a non-matching value
 * renders a quiet hint and is saved as typed. It never blocks a kiosk capture,
 * never gates a quote, and the kiosk server never validates against it (caps +
 * trim only). Routing-key area logic lives in shared/gazetteer.ts; this is just
 * "does it look like a full Eircode" for the capture fields.
 *
 * ⛔ THE PORTAL'S MIRROR GATES, AND THAT IS A COUPLING (board 2190, 2026-09-18).
 * `portal/src/shared/eircode.ts` carries this same pattern and REFUSES on it --
 * a nurse's "Somewhere else" destination must lead with a valid Eircode. So a
 * widening here widens what a nurse may submit, and a NARROWING here would
 * refuse destinations the portal accepts today. The portal keeps its own copy
 * because it cannot import from this package (no root package.json, no
 * tsconfig paths); `test/shared/eircode.spec.ts` grades the two for BEHAVIOUR,
 * not file text. Change this pattern only with that spec in view.
 *
 * ⚠️ `D6W` is the one routing key that is not letter-digit-digit, so it needs
 * its own alternative -- `D` is inside `[AC-FHKNPRTV-Y]` but `6W` is not
 * `[0-9]{2}`. Pinned as real at shared/gazetteer.ts:178,184 and
 * test/shared/routing-keys.spec.ts:22-23. Before board 2190 this pattern
 * refused `D6W PV38` outright, which was cosmetic while it only drove a hint
 * and would have made every Dublin 6W address unbookable once it gated.
 */
export const EIRCODE_HINT_RE =
  /^(?:D6W|[AC-FHKNPRTV-Y][0-9]{2})\s?[0-9AC-FHKNPRTV-Y]{4}$/i;

export function looksLikeEircode(value: string): boolean {
  return EIRCODE_HINT_RE.test(value.trim());
}

/**
 * Canonical STORE form for an eircode field (Vlad D2, 2026-07-19): uppercase,
 * with a single space after the 3-char routing key -- `c89ub9` -> `C89 UB9`.
 *
 * It NEVER rejects. This is the store-side companion to the advisory hint: the
 * form still accepts anything, and this canonicalises whatever landed so the
 * driver sheet stops showing `c89ub9` verbatim. Deliberately does NOT gate on
 * `looksLikeEircode` -- Vlad's own example is 6 chars and fails that 7-char
 * pattern, so gating would leave the canonical example un-spaced. A value that
 * is not eircode-shaped is simply uppercased (junk in an eircode field
 * survives; we never drop a customer's words).
 *
 * Returns null for null/undefined/blank so it drops straight into the
 * `x ?? null` store sites. Idempotent: normalising a canonical value is a no-op.
 */
export function normaliseEircode(value: string | null | undefined): string | null {
  if (value == null) return null;
  const compact = value.replace(/\s+/g, '').toUpperCase();
  if (compact === '') return null;
  if (compact.length <= 3) return compact;
  return `${compact.slice(0, 3)} ${compact.slice(3)}`;
}
